<template>
  <v-dialog
    style="z-index: 1000"
    v-model="dialog"
    width="80%"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    align="top"
  >
    <template v-slot:activator="{ on }">
      <v-btn v-bind="$attrs" icon v-on="on">
        <v-icon>{{ activatorIcon ? activatorIcon : "mdi-qrcode-scan" }}</v-icon>
      </v-btn>
    </template>

    <v-card>
      <v-toolbar>
        <v-toolbar-title> {{$vuetify.lang.t('$vuetify.label.scanQR') }} </v-toolbar-title>

        <v-spacer />

        <!-- Thermal labels in a dark corner of the plant read far better with
             the light on. Only Android exposes it; the button hides itself
             where the running track has no torch capability. -->
        <v-btn icon v-if="torchAvailable" @click="toggleTorch">
          <v-icon>{{ torchOn ? "mdi-flashlight-off" : "mdi-flashlight" }}</v-icon>
        </v-btn>

        <v-menu v-if="cameras && cameras.length > 1" offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on">
              <v-icon>mdi-camera</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              @click="switchCamera(camera)"
              v-for="camera in cameras"
              :key="camera.id"
            >
              <v-list-item-title>{{ camera.label }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-btn icon @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-progress-linear v-if="loading" indeterminate />

      <v-card-text class="pa-0">
        <!-- The surface is laid out at CAMERA size and only scaled down for
             the eye. See surfaceWidth below: this is what decides how much of
             the camera the decoder ever sees. -->
        <div class="scanner-frame" ref="frame" :style="frameStyle">
          <div :id="scannerId" :style="surfaceStyle"></div>
        </div>
        <!-- A camera that never starts used to leave the dialog blank with the
             progress bar running forever. Say what went wrong instead. -->
        <div v-if="cameraError" class="pa-4 text-center">
          <div class="mb-3">{{ cameraError }}</div>
          <v-btn small @click="openCamera">
            <v-icon left small>mdi-reload</v-icon>
            {{ $vuetify.lang.t('$vuetify.base.refresh') }}
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { decrypt } from "@/services/CryptoService.js";

import { mapActions, mapGetters } from "vuex";
import {
  Html5Qrcode,
  Html5QrcodeScannerState,
  Html5QrcodeSupportedFormats,
} from "html5-qrcode";

// The camera is remembered by LABEL, not by device id. iOS regenerates device
// ids between browsing sessions, so an id stored today names nothing tomorrow
// and the operator was asked to choose again on every scan.
const CAMERA_KEY = "qrScannerCameraLabel";

/**
 * How wide the preview is LAID OUT, which is how many camera pixels reach the
 * decoder — html5-qrcode draws the frame into a canvas the size of the
 * viewfinder in CSS pixels. Laid out at the width of the phone, that threw away
 * three quarters of every frame before anything looked at it, and a label had
 * to be held almost against the lens.
 *
 * Only where the browser has a hardware barcode detector — Android Chrome —
 * which eats a full frame for nothing. Safari has none and decodes in
 * JavaScript, so it keeps the plain layout until that is measured on a real
 * iPhone rather than assumed; 0 means "leave the surface at its natural size".
 */
const surfaceWidthForDevice = () =>
  typeof window !== "undefined" && "BarcodeDetector" in window ? 1280 : 0;

// A blocked or full store must not stop the scanner opening at all.
const rememberedCameraLabel = () => {
  try {
    return localStorage.getItem(CAMERA_KEY) || null;
  } catch (error) {
    return null;
  }
};

/**
 * Which of a phone's cameras to scan with.
 *
 * iPhones list "Front Camera", "Back Camera", "Back Dual Wide Camera", "Back
 * Ultra Wide Camera" and "Back Telephoto Camera". Only the plain "Back Camera"
 * is the main one; the ultra wide sees a label as a few dozen pixels and
 * focuses poorly up close, and "Dual Wide" switches to that ultra wide at
 * exactly the distance a label is held at. Asking for facingMode alone leaves
 * the choice to the browser, which does not always pick the main one.
 */
const pickCamera = (devices) => {
  if (!devices || devices.length === 0) {
    return null;
  }
  const label = (device) => (device && device.label ? device.label : "");
  const remembered = rememberedCameraLabel();
  const chosen = remembered && devices.find((d) => label(d) === remembered);
  if (chosen) {
    return chosen;
  }
  const mainBack = devices.find((d) => /^back camera$/i.test(label(d).trim()));
  if (mainBack) {
    return mainBack;
  }
  const isBack = (d) => /back|rear|environment/i.test(label(d));
  const isSpecialLens = (d) =>
    /ultra|tele|depth|truedepth|mono|dual/i.test(label(d));
  return (
    devices.find((d) => isBack(d) && !isSpecialLens(d)) ||
    devices.find(isBack) ||
    null
  );
};

/**
 * The camera scanner behind every QR button in the app.
 *
 * Two things decide whether a worker can scan a label at the first attempt,
 * and both are set here:
 *
 * - WHICH camera. A phone has several rear cameras and only the main one
 *   focuses on something held at arm's length. Picking by device id — the
 *   first whose label said "back" — handed iPhones their ultra-wide, where a
 *   label is a few dozen pixels and close focus is poor, and the scanner was
 *   unusable. `facingMode` lets the phone choose its default rear camera,
 *   which is the one with autofocus, and the camera menu is still there for
 *   the rare case where it has to be overridden.
 * - How many PIXELS reach the decoder. Not the camera's: html5-qrcode sizes
 *   its canvas to the qrbox — or to the whole viewfinder when there is none —
 *   in CSS pixels, and downsamples the frame into it. So a capture larger than
 *   720p adds nothing, and an aiming box is not free: it cuts the decoder's
 *   pixels and with them the distance a dense code can be read from. Our
 *   stickers run to 61 modules and need every pixel of the viewfinder.
 */
export default {
  props: {
    fps: {
      type: Number,
      required: false,
      default: 10,
    },
    activatorIcon: {
      type: String,
      required: false,
    },
    dialogWidth: {
      type: Number,
      required: false,
      default: 600,
    },
    manualProcess: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  data: () => ({
    dialog: false,
    cameras: [],
    // The camera actually in use, resolved from the device list each time the
    // dialog opens, since the ids behind those labels change.
    selectedCameraId: null,
    qrCode: null,
    loading: false,
    cameraError: "",
    torchAvailable: false,
    torchOn: false,
    starting: false,
    surfaceWidth: 960,
    displayScale: 1,
    frameHeight: 0,
  }),
  computed: {
    // Unique per instance. The id used to be the literal "qrScanner", which
    // was fine while only one component rendered a scanner. Now the header and
    // the worksheet entry both do, so getElementById returned the header's
    // hidden dialog and the entry's scanner streamed into it — camera running,
    // entry dialog blank.
    scannerId() {
      return `qrScanner-${this._uid}`;
    },
    // Both the stream request and the camera choice. html5-qrcode uses this in
    // place of a device id whenever it is valid.
    videoConstraints() {
      // 720p, not more: the frame is downsampled into a canvas the size of the
      // viewfinder before anything is decoded, so a larger capture buys no
      // detail where it counts and only slows the camera starting up.
      const constraints = {
        width: { ideal: 1280 },
        height: { ideal: 720 },
      };
      if (this.selectedCameraId) {
        constraints.deviceId = { exact: this.selectedCameraId };
      } else {
        constraints.facingMode = { ideal: "environment" };
      }
      return constraints;
    },
    // Laid out at camera width where that pays, and left alone where it does
    // not. scale() is paint-time only, so clientWidth — the one measurement the
    // library takes — stays the camera's.
    surfaceStyle() {
      if (!this.surfaceWidth) {
        return null;
      }
      return {
        width: `${this.surfaceWidth}px`,
        transform: `scale(${this.displayScale})`,
        transformOrigin: "top left",
      };
    },
    frameStyle() {
      if (!this.surfaceWidth || !this.frameHeight) {
        return null;
      }
      return { height: `${this.frameHeight}px` };
    },
    ...mapGetters("navigation", ["isAppLoading"]),
    ...mapGetters("auth", ["currentUser"]),
  },
  watch: {
    dialog(value) {
      if (value) {
        this.openCamera();
      } else {
        this.closeCamera();
      }
    },
  },
  methods: {
    ...mapActions("messages", [
        "addMessage",
        "addErrorMessage",
        "addSuccessMessage"
      ]),
    async openCamera() {
      // Opening twice — a reopened dialog, a retry, a camera switch — used to
      // reach start() while the library still considered the previous scan
      // live, which it refuses with "Cannot clear while scan is ongoing".
      if (this.starting) {
        return;
      }
      this.starting = true;
      this.cameraError = "";
      this.loading = true;
      // The dialog body is only in the DOM once the dialog is open, and
      // Html5Qrcode resolves its element by id in the constructor.
      await this.$nextTick();
      this.sizeSurface();
      try {
        if (!this.qrCode) {
          this.qrCode = new Html5Qrcode(this.scannerId, {
            formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
          });
        }
        // The list carries the labels, and the label is what tells the main
        // rear camera from the ultra wide. Getting it first costs one call and
        // decides the whole scan.
        await this.listCameras();
        try {
          await this.startScanning();
        } catch (error) {
          // A device id can go stale between the list and the start. Fall back
          // to whatever the phone considers its rear camera rather than
          // dead-ending on a blank dialog.
          if (!this.selectedCameraId) {
            throw error;
          }
          this.selectedCameraId = null;
          await this.startScanning();
        }
      } catch (error) {
        // Whatever state the library was left in, it is not one we can start
        // again — reusing it is what produced "Cannot clear while scan is
        // ongoing" on the next open.
        this.qrCode = null;
        // The library reports the useful cases itself — permission refused, no
        // camera, and a page served over plain http, where the browser hides
        // the camera altogether. Keep its wording rather than flattening every
        // cause into one message.
        this.cameraError =
          typeof error === "string"
            ? error
            : (error && error.message) || "Unable to start the camera";
      } finally {
        this.loading = false;
        this.starting = false;
      }
    },
    startScanning() {
      return this.qrCode
        .start(
          this.videoConstraints,
          {
            fps: this.fps,
            // No qrbox on purpose. html5-qrcode sizes its decode canvas to the
            // qrbox in VIEWFINDER pixels and downsamples the camera frame into
            // it, so a box is not an aiming aid that costs nothing — it is a
            // straight cut in the pixels the decoder gets, and with it the
            // distance a dense code can be read from. Left unset, the decode
            // region is the whole viewfinder.
            //
            // Every frame that fails is otherwise decoded a second time
            // mirrored, which doubles the work for a case we do not have:
            // nobody scans our labels through a mirror.
            disableFlip: true,
            videoConstraints: this.videoConstraints,
          },
          (qrCodeMessage) => {
            this.processQrCode(qrCodeMessage);
            this.dialog = false;
          },
          // Called for every frame that does not decode, which at 10 fps is
          // most of them. Nothing to do but wait for the next one.
          () => {}
        )
        .then(() => {
          this.detectTorch();
          this.fitFrame();
        });
    },
    // Must run before the scanner starts: it is the element's width at that
    // moment that fixes the decode canvas for the whole session.
    sizeSurface() {
      this.surfaceWidth = surfaceWidthForDevice();
      if (!this.surfaceWidth) {
        this.displayScale = 1;
        this.frameHeight = 0;
        return;
      }
      const frame = this.$refs.frame;
      const available = frame && frame.clientWidth ? frame.clientWidth : 0;
      this.displayScale = available
        ? Math.min(1, available / this.surfaceWidth)
        : 1;
      // Something to show before the stream reports its real shape, so the
      // dialog does not open as an empty white sheet.
      this.frameHeight = Math.round(this.surfaceWidth * 0.75 * this.displayScale);
    },
    // The frame clips the oversized surface, so its height is the preview the
    // operator sees. Measured from the running video — but the video has no
    // height until the stream delivers its first frame, and start() can resolve
    // before that. Measuring once left the frame at zero: the camera ran and
    // decoded perfectly behind a preview nobody could see.
    fitFrame(attempt) {
      if (!this.surfaceWidth) {
        return;
      }
      const surface = document.getElementById(this.scannerId);
      const frame = this.$refs.frame;
      if (!surface || !frame) {
        return;
      }
      // Re-measure the frame too, not just the video. The dialog animates in,
      // so its width a tick after opening is not the width it settles at, and
      // a scale taken from the wrong one shows a corner of a huge video or a
      // stamp-sized preview.
      if (frame.clientWidth) {
        this.displayScale = Math.min(1, frame.clientWidth / this.surfaceWidth);
      }
      if (surface.clientHeight) {
        this.frameHeight = Math.round(surface.clientHeight * this.displayScale);
      }
      // Keep correcting for a second: the stream, the dialog transition and
      // the address bar settling all land at different moments.
      if ((attempt || 0) < 10) {
        setTimeout(() => this.fitFrame((attempt || 0) + 1), 100);
      }
    },
    async closeCamera() {
      this.frameHeight = 0;
      this.torchOn = false;
      this.torchAvailable = false;
      if (
        !this.qrCode ||
        this.qrCode.getState() === Html5QrcodeScannerState.NOT_STARTED
      ) {
        return;
      }
      try {
        await this.qrCode.stop();
      } catch (error) {
        // Nothing the operator can act on: the dialog is closing and the
        // stream goes with it.
        console.log(error);
      }
    },
    // Also fills the menu, which is the operator's override when the choice
    // below is still wrong on some phone we have not seen.
    listCameras() {
      return Html5Qrcode.getCameras()
        .then((devices) => {
          this.cameras = devices || [];
          const camera = pickCamera(this.cameras);
          this.selectedCameraId = camera ? camera.id : null;
        })
        .catch(() => {
          // No list — scanning can still start on facingMode alone.
          this.cameras = [];
          this.selectedCameraId = null;
        });
    },
    async switchCamera(camera) {
      if (!camera || !camera.id || camera.id === this.selectedCameraId) {
        return;
      }
      this.selectedCameraId = camera.id;
      try {
        localStorage.setItem(CAMERA_KEY, camera.label || "");
      } catch (error) {
        // Private mode or a full store: the choice just lasts this session.
      }
      await this.closeCamera();
      this.loading = true;
      try {
        await this.startScanning();
        this.cameraError = "";
      } catch (error) {
        this.cameraError =
          typeof error === "string"
            ? error
            : (error && error.message) || "Unable to start the camera";
      } finally {
        this.loading = false;
      }
    },
    detectTorch() {
      try {
        const capabilities = this.qrCode.getRunningTrackCapabilities();
        this.torchAvailable = !!(capabilities && "torch" in capabilities);
      } catch (error) {
        this.torchAvailable = false;
      }
    },
    async toggleTorch() {
      const next = !this.torchOn;
      try {
        await this.qrCode.applyVideoConstraints({
          advanced: [{ torch: next }],
        });
        this.torchOn = next;
      } catch (error) {
        // Some devices advertise torch and then refuse it.
        this.torchAvailable = false;
      }
    },
    processQrCode(qrCodeResult) {
      const isUrl = this.isValidHttpUrl(qrCodeResult);
      if (isUrl) {
        if(!this.manualProcess){
          this.$router.push({ name: "QrDispatcher", params: {url: qrCodeResult} });
        }
        this.$emit("captured", qrCodeResult);
      } else {
        // Decrypt
        const decryptedData = decrypt(qrCodeResult);
        if(!this.manualProcess){
          this.$router.push({ name: "QrDispatcher", params: {data: decryptedData} });
        }
        this.$emit("captured", decryptedData);
      }
    },
    isValidHttpUrl(string) {
      try {
        new URL(string);
        return true;
      } catch (_) {
        return false;
      }
    },
  },
};
</script>

<style scoped>
/* The surface inside is wider than the screen on purpose. Clip it, and give
   the frame the height the scaled surface actually occupies so the dialog does
   not reserve camera-sized space. */
.scanner-frame {
  position: relative;
  width: 100%;
  overflow: hidden;
}

/* scale() is a paint-time transform: it does NOT change clientWidth, which is
   the only thing html5-qrcode measures. That is what lets the decoder work on
   full-resolution pixels while the operator sees a normal preview. */
.scanner-surface {
  transform-origin: top left;
}
</style>
